<script setup>
/* global axios */
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import IspDataRow from './IspDataRow.vue';
import IspEditableField from './IspEditableField.vue'; // GK-HUB: Editable protocol field
import IspStatusBadge from './IspStatusBadge.vue';
import IspSignalBar from './IspSignalBar.vue';
import {
  getConnectionBadgeClasses,
  getMotivoBadgeClasses,
  getCsatRatingInfo,
  renderStars,
} from './useIspStatusStyles';

const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
const attrs = computed(() => currentChat.value?.custom_attributes || {});

const connectionClasses = computed(() =>
  getConnectionBadgeClasses(attrs.value.status_conexao)
);

const motivoClasses = computed(() =>
  getMotivoBadgeClasses(attrs.value.motivo_contato)
);

// GK-HUB: Contact Reasons picker
const assignedReasons = ref([]);
const showDropdown = ref(false);
const dropdownRef = ref(null);
const motivosRef = ref(null);
const motivosExpanded = ref(false);

const allReasons = computed(
  () => store.getters['contactReasons/getActiveReasons'] || []
);
const availableReasons = computed(() => {
  const assignedIds = new Set(
    assignedReasons.value.map(r => r.contact_reason_id)
  );
  return allReasons.value.filter(r => !assignedIds.has(r.id));
});

const visibleReasons = computed(() => {
  if (assignedReasons.value.length <= 1) {
    return assignedReasons.value;
  }
  return [];
});

const accountId = useMapGetter('getCurrentAccountId');

const loadReasons = async () => {
  const convId = currentChat.value?.id;
  if (!convId || !accountId.value) return;
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${accountId.value}/conversations/${convId}/contact_reasons`
    );
    assignedReasons.value = data;
  } catch {
    assignedReasons.value = [];
  }
};

const addReason = async reasonId => {
  const convId = currentChat.value?.id;
  if (!convId) return;
  showDropdown.value = false;
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${accountId.value}/conversations/${convId}/contact_reasons`,
      { contact_reason_id: reasonId }
    );
    assignedReasons.value.push(data);
  } catch {
    // ignore
  }
};

const removeReason = async ccrId => {
  const convId = currentChat.value?.id;
  if (!convId) return;
  try {
    await axios.delete(
      `/api/v1/accounts/${accountId.value}/conversations/${convId}/contact_reasons/${ccrId}`
    );
    assignedReasons.value = assignedReasons.value.filter(r => r.id !== ccrId);
  } catch {
    // ignore
  }
};

const handleClickOutside = event => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    showDropdown.value = false;
  }
  if (
    motivosExpanded.value &&
    motivosRef.value &&
    !motivosRef.value.contains(event.target)
  ) {
    motivosExpanded.value = false;
  }
};

onMounted(() => {
  store.dispatch('contactReasons/get');
  loadReasons();
  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});

watch(
  () => currentChat.value?.id,
  () => {
    motivosExpanded.value = false;
    loadReasons();
  }
);
// GK-HUB: end Contact Reasons picker

// Parse "1042:200 Mega Fibra:online|1043:100 Mega:offline" into array of objects
const contratos = computed(() => {
  const raw = attrs.value.contratos_resumo;
  if (!raw) return [];
  return String(raw)
    .split('|')
    .map(seg => {
      const parts = seg.trim().split(':');
      if (parts.length < 2) return null;
      return {
        id: parts[0]?.trim() || '',
        plano: parts[1]?.trim() || '',
        status: parts[2]?.trim()?.toLowerCase() || '',
      };
    })
    .filter(Boolean);
});

const activeContratoId = computed(() => attrs.value.contrato_id || '');

const hasMultipleContratos = computed(() => contratos.value.length > 1);

// GK-HUB: Sidebar CSAT — this conversation's rating
const csatResponse = computed(() => currentChat.value?.csat_survey_response);
const csatInfo = computed(() =>
  csatResponse.value ? getCsatRatingInfo(csatResponse.value.rating) : null
);
// GK-HUB: end Sidebar CSAT

const getStatusDotClass = status => {
  const map = {
    online: 'bg-n-teal-9',
    offline: 'bg-n-ruby-9',
    bloqueado: 'bg-n-ruby-9',
    instavel: 'bg-n-amber-9',
  };
  return map[status] || 'bg-n-slate-9';
};

const getStatusGlowClass = status => {
  const map = {
    online: 'shadow-[0_0_6px_rgba(93,173,226,0.4)]',
    offline: 'shadow-[0_0_6px_rgba(239,68,68,0.3)]',
    bloqueado: 'shadow-[0_0_6px_rgba(239,68,68,0.3)]',
    instavel: 'shadow-[0_0_6px_rgba(245,158,11,0.3)]',
  };
  return map[status] || '';
};

// GK-HUB: Editable protocol field (Phase 2)
const saveProtocolo = async newValue => {
  const convId = currentChat.value?.id;
  if (!convId) return;
  await store.dispatch('updateCustomAttributes', {
    conversationId: convId,
    customAttributes: { protocolo: newValue },
  });
};
// GK-HUB: end editable protocol
</script>

<template>
  <div class="flex flex-col px-4 py-1">
    <IspDataRow label="Plano" :value="attrs.plano" />
    <IspDataRow
      label="Contrato"
      :value="activeContratoId ? `#${activeContratoId}` : ''"
      mono
    />
    <IspDataRow label="Conexão">
      <IspStatusBadge
        :label="attrs.status_conexao"
        :bg="connectionClasses.bg"
        :text="connectionClasses.text"
        :dot="connectionClasses.dot"
        :ring="connectionClasses.ring"
      />
    </IspDataRow>
    <IspDataRow label="IP" :value="attrs.ip_address" mono />
    <IspDataRow label="ONU" :value="attrs.onu_serial" mono />
    <IspDataRow label="Sinal ONU">
      <IspSignalBar :d-bm="attrs.sinal_onu" />
    </IspDataRow>
    <!-- GK-HUB: Contact Reasons picker (multi-motivo v2 — list design) -->
    <div ref="motivosRef" class="group/motivos">
      <IspDataRow
        label="Motivos"
        :no-divider="motivosExpanded && assignedReasons.length > 1"
      >
        <div class="flex items-center gap-1.5 justify-end min-h-[18px]">
          <!-- Collapsed: "N motivos ˅" quando 2+ motivos -->
          <!-- eslint-disable vue/no-bare-strings-in-template -->
          <button
            v-if="assignedReasons.length > 1 && !motivosExpanded"
            class="inline-flex items-center gap-1 px-1.5 py-0 rounded-full text-[10px] font-medium leading-[18px] bg-n-slate-3 text-n-slate-11 border border-n-weak hover:bg-n-slate-4 transition-colors"
            @click.stop="motivosExpanded = true"
          >
            <span>{{ assignedReasons.length }} motivos</span>
            <span class="text-[8px] leading-none">▼</span>
          </button>
          <!-- Expanded: "N motivos ˄" quando 2+ motivos -->
          <button
            v-if="assignedReasons.length > 1 && motivosExpanded"
            class="inline-flex items-center gap-1 px-1.5 py-0 rounded-full text-[10px] font-medium leading-[18px] bg-n-slate-3 text-n-slate-11 border border-n-weak hover:bg-n-slate-4 transition-colors"
            @click.stop="motivosExpanded = false"
          >
            <span>{{ assignedReasons.length }} motivos</span>
            <span class="text-[8px] leading-none">▲</span>
          </button>
          <!-- eslint-enable vue/no-bare-strings-in-template -->
          <!-- 1 motivo: badge inline com × -->
          <span
            v-for="r in visibleReasons"
            :key="r.id"
            class="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium leading-[18px] max-w-[140px]"
            :style="
              r.color
                ? `background:${r.color}20; color:${r.color}; box-shadow:inset 0 0 0 1px ${r.color}40`
                : undefined
            "
            :class="
              !r.color ? 'bg-n-blue-3 text-n-blue-11 ring-1 ring-n-blue-6' : ''
            "
          >
            <span class="truncate">{{ r.name }}</span>
            <!-- eslint-disable vue/no-bare-strings-in-template -->
            <button
              class="hover:opacity-70 ml-0.5 shrink-0 text-[9px] leading-none"
              @click="removeReason(r.id)"
            >
              ×
            </button>
            <!-- eslint-enable vue/no-bare-strings-in-template -->
          </span>
          <!-- 0 motivos: dash -->
          <span
            v-if="!assignedReasons.length && !attrs.motivo_contato"
            class="text-xs font-medium text-n-slate-12"
          >
            —
          </span>
          <!-- Fallback: legacy motivo_contato -->
          <IspStatusBadge
            v-if="!assignedReasons.length && attrs.motivo_contato"
            :label="attrs.motivo_contato"
            :bg="motivoClasses.bg"
            :text="motivoClasses.text"
            :ring="motivoClasses.ring"
          />
          <!-- ⊕ dropdown para adicionar motivo -->
          <div
            v-if="availableReasons.length"
            ref="dropdownRef"
            class="relative transition-opacity"
            :class="
              !assignedReasons.length
                ? 'opacity-0 group-hover/motivos:opacity-100'
                : ''
            "
          >
            <button
              class="w-5 h-5 rounded-full bg-n-blue-3 hover:bg-n-blue-4 border border-n-blue-6 flex items-center justify-center transition-colors"
              @click.stop="showDropdown = !showDropdown"
            >
              <span class="text-sm font-bold leading-none text-n-blue-11"
                >+</span
              >
            </button>
            <div
              v-if="showDropdown"
              class="absolute z-50 top-6 right-0 w-48 rounded-lg border border-n-weak bg-n-solid-2 shadow-lg py-1 max-h-40 overflow-y-auto"
            >
              <button
                v-for="opt in availableReasons"
                :key="opt.id"
                class="w-full text-left px-3 py-1.5 text-[12px] text-n-slate-12 hover:bg-n-blue-3 transition-colors flex items-center gap-2"
                @click="addReason(opt.id)"
              >
                <span
                  v-if="opt.color"
                  class="w-2.5 h-2.5 rounded-full shrink-0"
                  :style="`background:${opt.color}`"
                />
                {{ opt.name }}
              </button>
            </div>
          </div>
        </div>
      </IspDataRow>
      <!-- Expanded: lista vertical com bordas coloridas -->
      <div
        v-if="assignedReasons.length > 1 && motivosExpanded"
        class="pb-1.5 border-b border-n-weak/50"
      >
        <div
          class="rounded-lg border border-n-weak bg-n-slate-2 overflow-hidden"
        >
          <div
            v-for="(r, idx) in assignedReasons"
            :key="r.id"
            class="group/item flex items-center py-1.5 pr-2 relative transition-colors hover:bg-n-slate-3/50"
            :class="
              idx < assignedReasons.length - 1
                ? 'border-b border-n-weak/30'
                : ''
            "
          >
            <!-- Borda lateral colorida -->
            <div
              class="absolute left-0 top-1 bottom-1 w-[3px] rounded-r-sm"
              :style="r.color ? `background:${r.color}` : ''"
              :class="!r.color ? 'bg-n-blue-9' : ''"
            />
            <!-- Dot -->
            <span
              class="w-1.5 h-1.5 rounded-full shrink-0 ml-2.5 mr-2"
              :style="r.color ? `background:${r.color}` : ''"
              :class="!r.color ? 'bg-n-blue-9' : ''"
            />
            <!-- Nome -->
            <span
              class="text-[11px] font-medium text-n-slate-12 flex-1 truncate"
            >
              {{ r.name }}
            </span>
            <!-- Botão remover (× circular, sempre visível) -->
            <button
              class="w-5 h-5 rounded-full flex items-center justify-center shrink-0 ml-1 bg-n-slate-3 border border-n-weak hover:bg-n-ruby-3 hover:border-n-ruby-6 transition-all"
              @click="removeReason(r.id)"
            >
              <!-- eslint-disable vue/no-bare-strings-in-template -->
              <span class="text-xs font-bold leading-none text-n-slate-11"
                >×</span
              >
              <!-- eslint-enable vue/no-bare-strings-in-template -->
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- GK-HUB: end Contact Reasons picker -->
    <!-- GK-HUB: Editable protocol field (Phase 2) -->
    <IspEditableField
      label="Protocolo"
      :value="attrs.protocolo"
      mono
      :maxlength="50"
      :no-divider="!hasMultipleContratos && !csatResponse"
      @save="saveProtocolo"
    />

    <!-- Contratos do cliente - card -->
    <div
      v-if="hasMultipleContratos"
      class="mt-2 mb-1 rounded-lg border border-n-weak bg-n-slate-2 overflow-hidden"
    >
      <!-- Header -->
      <div class="px-3 py-1.5 border-b border-n-weak/50">
        <span class="text-[10px] text-n-slate-10 uppercase tracking-wider">
          {{ $t('CONVERSATION_SIDEBAR.ACCORDION.ISP_CONTRATOS_TITLE') }}
        </span>
      </div>
      <!-- Contract rows -->
      <div class="flex flex-col">
        <div
          v-for="(c, idx) in contratos"
          :key="c.id"
          class="flex items-center gap-2 px-3 py-1.5 transition-colors"
          :class="[
            c.id === activeContratoId ? 'bg-n-blue-3/40' : '',
            idx < contratos.length - 1 ? 'border-b border-n-weak/30' : '',
          ]"
        >
          <!-- Status dot -->
          <span
            class="w-1.5 h-1.5 rounded-full shrink-0"
            :class="[getStatusDotClass(c.status), getStatusGlowClass(c.status)]"
          />
          <!-- Contract ID -->
          <span
            class="font-mono text-[10px] shrink-0"
            :class="
              c.id === activeContratoId
                ? 'text-n-blue-11 font-semibold'
                : 'text-n-slate-10'
            "
          >
            #{{ c.id }}
          </span>
          <!-- Plan name -->
          <span
            class="text-[11px] truncate flex-1"
            :class="
              c.id === activeContratoId
                ? 'text-n-slate-12 font-medium'
                : 'text-n-slate-11'
            "
          >
            {{ c.plano }}
          </span>
          <!-- Active indicator -->
          <span
            v-if="c.id === activeContratoId"
            class="text-[9px] text-n-blue-11 bg-n-blue-3 px-1.5 py-0.5 rounded-full font-semibold shrink-0 ring-1 ring-n-blue-6"
          >
            {{ $t('CONVERSATION_SIDEBAR.ACCORDION.ISP_CONTRATO_ATIVO') }}
          </span>
        </div>
      </div>
    </div>
    <!-- GK-HUB: Sidebar CSAT — this conversation's rating -->
    <div v-if="csatResponse" class="mt-1">
      <IspDataRow
        :label="$t('CONVERSATION_SIDEBAR.ACCORDION.ISP_AVALIACAO')"
        no-divider
      >
        <div class="flex items-center gap-1.5">
          <span class="text-xs tracking-wider" :class="csatInfo.star">
            {{ renderStars(csatResponse.rating) }}
          </span>
          <span
            class="text-[10px] font-medium px-1.5 py-0.5 rounded-full"
            :class="[csatInfo.bg, csatInfo.text]"
          >
            {{ csatInfo.label }}
          </span>
        </div>
      </IspDataRow>
      <!-- eslint-disable vue/no-bare-strings-in-template -->
      <div
        v-if="csatResponse.feedback_message"
        class="text-[11px] text-n-slate-11 italic px-0 pb-1 leading-4"
      >
        "{{ csatResponse.feedback_message }}"
      </div>
      <!-- eslint-enable vue/no-bare-strings-in-template -->
    </div>
    <!-- GK-HUB: end Sidebar CSAT -->
  </div>
</template>
